import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Brain, ArrowLeft, Search, Phone, BarChart3, Mic, FileText,
  TrendingUp, Users, Target, ListChecks, Star, ChevronDown, ChevronUp,
  Sparkles, Clock, Zap, Shield, ArrowRight, Loader2, Terminal, Cpu, Code2,
  ClipboardList, GraduationCap, ExternalLink, Award
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: "easeOut" }
  })
};

const ICON_MAP = {
  "Sentiment Analysis": BarChart3, "Call Transcription": Mic, "Key Topic Extraction": Target,
  "Action Item Detection": ListChecks, "Agent Performance Scoring": Star, "Trend Reports": TrendingUp,
  "Behavioral Analytics": TrendingUp,
  "In-Browser Python (Pyodide)": Terminal, "Auto-Graded Lessons": Zap, "AI Tutor (Claude Sonnet 4.5)": Sparkles,
  "Gamified Progress": Star, "Real Python Curriculum": Code2, "Zero-Install Setup": Shield,
  default: Zap
};
const CATEGORY_GRADIENTS = {
  "Analytics": "from-[#00F0FF] to-[#0070FF]",
  "Productivity": "from-[#7000FF] to-[#00F0FF]",
  "Content": "from-amber-400 to-orange-500",
  "Operations": "from-[#7EE787] to-[#43A047]",
  default: "from-[#00F0FF] to-[#7000FF]"
};

// Tool-name → icon (kebab-case match on tool_url)
const TOOL_ICON = {
  "/ai-tools/call-analyzer": Phone,
  "/ai-tools/python-lab": Terminal,
  "/ai-tools/seo-services": Search,
  "/ai-tools/sop-studio": ClipboardList,
  "/ai-tools/resume-ats": Award,
  "https://shansotutor.com": GraduationCap,
};
const CATEGORY_ICON = {
  "Analytics": BarChart3,
  "Productivity": Cpu,
  "Content": FileText,
  "Operations": ClipboardList,
};

function getToolIcon(tool) {
  if (TOOL_ICON[tool.tool_url]) return TOOL_ICON[tool.tool_url];
  if (CATEGORY_ICON[tool.category]) return CATEGORY_ICON[tool.category];
  return Sparkles;
}

function StatusBadge({ status }) {
  const config = {
    "live": { label: "Live", bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-400" },
    "coming-soon": { label: "Coming Soon", bg: "bg-amber-500/15", text: "text-amber-400", dot: "bg-amber-400" },
    "beta": { label: "Beta", bg: "bg-purple-500/15", text: "text-purple-400", dot: "bg-purple-400" }
  };
  const c = config[status] || config["coming-soon"];
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} animate-pulse`} />
      {c.label}
    </span>
  );
}

function PricingDisplay({ pricing, status }) {
  if (!pricing) {
    return (
      <div className="flex items-baseline gap-1">
        <span className="text-zinc-400 text-sm font-medium">Pricing coming soon</span>
      </div>
    );
  }
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-3xl font-bold text-white">&#8377;{pricing.amount.toLocaleString()}</span>
      <span className="text-zinc-500 text-sm">/{pricing.period}</span>
    </div>
  );
}

function ToolCard({ tool, isExpanded, onToggle }) {
  const gradient = CATEGORY_GRADIENTS[tool.category] || CATEGORY_GRADIENTS.default;
  const features = tool.features || [];
  const ToolIcon = getToolIcon(tool);

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="bg-[#0d0d14] border border-zinc-800/60 rounded-2xl overflow-hidden hover:border-zinc-700 transition-all duration-300 group"
      data-testid={`tool-card-${tool.id}`}
    >
      {/* Card Header */}
      <div className="p-6 pb-0">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
            <ToolIcon size={28} className="text-white" />
          </div>
          <StatusBadge status={tool.status} />
        </div>

        <h3 className="text-xl font-bold text-white font-outfit mb-2">{tool.name}</h3>
        <p className="text-[#00F0FF] text-sm font-medium mb-3">{tool.tagline}</p>
        <p className="text-zinc-400 text-sm leading-relaxed">{tool.description}</p>
      </div>

      {/* Pricing */}
      <div className="px-6 py-4">
        <PricingDisplay pricing={tool.monthly_price ? { amount: tool.monthly_price, period: "month" } : null} status={tool.status} />
      </div>

      {/* Features preview (first 3) */}
      <div className="px-6 pb-2">
        <div className="grid gap-3">
          {features.slice(0, 3).map((feat, idx) => {
            const FeatIcon = ICON_MAP[feat.title] || ICON_MAP.default;
            return (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-zinc-800/80 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FeatIcon size={16} className="text-[#00F0FF]" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{feat.title}</p>
                  <p className="text-zinc-500 text-xs leading-relaxed">{feat.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Expandable section */}
      {isExpanded && features.length > 3 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="px-6 pb-2"
        >
          <div className="grid gap-3 mt-3 pt-3 border-t border-zinc-800/50">
            {features.slice(3).map((feat, idx) => {
              const FeatIcon = ICON_MAP[feat.title] || ICON_MAP.default;
              return (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-zinc-800/80 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FeatIcon size={16} className="text-[#7000FF]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{feat.title}</p>
                    <p className="text-zinc-500 text-xs leading-relaxed">{feat.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {tool.use_cases?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-zinc-800/50">
              <p className="text-zinc-300 text-sm font-semibold mb-3">Use Cases</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {tool.use_cases.map((uc, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00F0FF]" />
                    <span className="text-zinc-400 text-xs">{uc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Expand/Collapse + CTA */}
      <div className="px-6 py-4 flex items-center justify-between border-t border-zinc-800/40 mt-2">
        {features.length > 3 ? (
          <button onClick={onToggle} className="flex items-center gap-1.5 text-[#00F0FF] text-sm font-medium hover:text-white transition-colors" data-testid={`tool-expand-${tool.id}`}>
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {isExpanded ? "Show Less" : "View All Features"}
          </button>
        ) : <div />}

        <a
          href={tool.tool_url || `https://wa.me/919962536379?text=I'm%20interested%20in%20${encodeURIComponent(tool.name)}`}
          {...(tool.tool_url && /^https?:\/\//i.test(tool.tool_url) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
          data-testid={`tool-cta-${tool.id}`}
        >
          {tool.status === "live" ? "Try Now" : tool.tool_url ? "Try Now" : "Get Notified"}
          {tool.tool_url && /^https?:\/\//i.test(tool.tool_url) ? <ExternalLink size={14} /> : <ArrowRight size={16} />}
        </a>
      </div>
    </motion.div>
  );
}

export default function AIToolsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [expandedTools, setExpandedTools] = useState({});
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ai-tools-catalog/public`).then(r => {
      setTools(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const categories = ["All", ...new Set(tools.map(t => t.category).filter(Boolean))];

  const filteredTools = tools.filter(tool => {
    const matchesSearch = search === "" ||
      tool.name.toLowerCase().includes(search.toLowerCase()) ||
      tool.description.toLowerCase().includes(search.toLowerCase()) ||
      (tool.features || []).some(f => f.title.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = activeCategory === "All" || tool.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleExpand = (id) => {
    setExpandedTools(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#00F0FF] rounded-sm flex items-center justify-center">
              <Brain size={18} className="text-black" />
            </div>
            <span className="font-outfit font-bold text-[#00F0FF] text-lg tracking-tight">ShansoAI<span className="text-white">Hub</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="hidden sm:flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors"
            >
              <ArrowLeft size={16} /> Back to Home
            </button>
            <a
              href="https://wa.me/919962536379"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#00F0FF] text-black font-bold text-sm px-5 py-2 hover:bg-white transition-colors duration-300 font-dm-sans"
            >
              Contact Us
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-to-b from-[#00F0FF]/8 via-[#7000FF]/5 to-transparent rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <div className="inline-flex items-center gap-2 bg-[#00F0FF]/10 border border-[#00F0FF]/20 rounded-full px-4 py-1.5 mb-6">
              <Sparkles size={14} className="text-[#00F0FF]" />
              <span className="text-[#00F0FF] text-xs font-semibold tracking-wider uppercase">AI-Powered Solutions</span>
            </div>
          </motion.div>

          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-outfit leading-tight mb-5"
          >
            AI Tools Built for{" "}
            <span className="bg-gradient-to-r from-[#00F0FF] to-[#7000FF] bg-clip-text text-transparent">
              Business Impact
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Production-ready AI tools designed to automate workflows, extract insights, and drive real business outcomes. Built by ShansoAI.
          </motion.p>

          {/* Stats */}
          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="flex justify-center gap-8 sm:gap-16"
          >
            {[
              { icon: Zap, value: tools.length, label: "AI Tools" },
              { icon: Shield, value: "Enterprise", label: "Grade Security" },
              { icon: Clock, value: "24/7", label: "Availability" }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <stat.icon size={20} className="text-[#00F0FF] mx-auto mb-1.5" />
                <p className="text-white font-bold text-lg">{stat.value}</p>
                <p className="text-zinc-500 text-xs">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="px-6 pb-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                placeholder="Search tools, features..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-[#0d0d14] border border-zinc-800 rounded-xl text-white text-sm placeholder-zinc-600 focus:border-[#00F0FF] focus:outline-none transition-colors"
                data-testid="ai-tools-search"
              />
            </div>

            {/* Category filters */}
            <div className="flex gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    activeCategory === cat
                      ? "bg-[#00F0FF] text-black"
                      : "bg-[#0d0d14] text-zinc-400 border border-zinc-800 hover:border-zinc-600"
                  }`}
                  data-testid={`filter-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto">
          {filteredTools.length === 0 ? (
            <div className="text-center py-20">
              <Search size={48} className="text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-lg">No tools found matching your search.</p>
              <button
                onClick={() => { setSearch(""); setActiveCategory("All"); }}
                className="mt-4 text-[#00F0FF] text-sm hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
              {filteredTools.map((tool) => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  isExpanded={!!expandedTools[tool.id]}
                  onToggle={() => toggleExpand(tool.id)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-[#0d0d1a] to-[#0a0a14] border border-zinc-800/60 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#00F0FF]/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#7000FF]/5 rounded-full blur-3xl" />

            <div className="relative">
              <h2 className="text-2xl sm:text-3xl font-bold font-outfit mb-4">
                Have a custom AI tool in mind?
              </h2>
              <p className="text-zinc-400 text-sm sm:text-base mb-8 max-w-lg mx-auto">
                We build custom AI solutions tailored to your business needs. From call analytics to document processing — let's discuss your requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://wa.me/919962536379?text=I'm%20interested%20in%20a%20custom%20AI%20tool"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black font-bold rounded-lg hover:opacity-90 transition-opacity"
                  data-testid="custom-tool-cta"
                >
                  Chat on WhatsApp <ArrowRight size={18} />
                </a>
                <a
                  href="mailto:support@shansoai.com?subject=Custom AI Tool Inquiry"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-zinc-700 text-white font-medium rounded-lg hover:border-[#00F0FF] transition-colors"
                >
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800/60 py-8 px-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#00F0FF] rounded-sm flex items-center justify-center">
              <Brain size={14} className="text-black" />
            </div>
            <span className="font-outfit font-bold text-[#00F0FF] text-sm">ShansoAI<span className="text-white">Hub</span></span>
          </div>
          <p className="text-zinc-600 text-xs">&copy; {new Date().getFullYear()} ShansoAI Hub. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-zinc-500 text-xs hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-zinc-500 text-xs hover:text-white transition-colors">Terms</Link>
            <Link to="/contact" className="text-zinc-500 text-xs hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
